import { MainInner } from "@/styles/global";
import {
  DetailsBig,
  PrimaryButtonCss,
  TextLinkCss,
  h2,
  h5,
  h6Css,
} from "@/styles/typography";
import styled from "styled-components";

const Inner = styled(MainInner)`
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    gap: 32px;
  }
`;

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const H5 = styled(h5)``;

const InputsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    flex-direction: row;
    gap: 28px;
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;

  &:after {
    content: "";
    position: absolute;
    right: 20px;
    top: 42%;
    border-right: 3px solid ${({ theme }) => theme.colors.textBody};
    border-bottom: 3px solid ${({ theme }) => theme.colors.textBody};
    border-radius: 2px;
    height: 10px;
    width: 10px;
    transform: translateY(-50%) rotate(45deg);
    pointer-events: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: fit-content;
  }

  .custom-select-header {
    width: 100%;
    padding: 16px 25px;
    border-radius: 4px;
  }

  .custom-select-options {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.coreLight};
    border-radius: 4px;
    z-index: 1;
  }

  .custom-select-option {
    padding: 16px 25px;
    cursor: pointer;
    font-size: 15px;
  }

  .custom-select-option:hover {
    background-color: ${({ theme }) => theme.colors.lightPurple};
    border-radius: 4px;
  }
`;

const Select = styled.div`
  border: none;
  display: flex;
  border-radius: 4px;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: ${({ theme }) => theme.colors.coreLight};
  appearance: none;
  cursor: pointer;
  ${TextLinkCss}

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 250px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    max-width: 550px;
  }
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;

  svg {
    position: absolute;
    top: 50%;
    left: 16px;
    transform: translateY(-50%);
  }
`;

const SearchInput = styled.input`
  border: none;
  border-radius: 4px;
  display: flex;
  padding: 15px 20px 15px 48px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: ${({ theme }) => theme.colors.coreLight};
  ${TextLinkCss}
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textHeading};
  }
`;

const SearchButton = styled.button`
  margin-top: 24px;
  width: 100% !important;
  display: block;
  ${PrimaryButtonCss}

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 429px;
    max-width: fit-content;
    margin-top: 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const H2 = styled(h2)`
  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    margin-top: 68px;
  }
`;

const DetailsLarge = styled(DetailsBig)``;

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 32px;
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-top: 40px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-top: 60px;
  }

  .hide-in-mobile {
    display: none;

    @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      display: block;
    }
  }
`;

const ArrowLeft = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  height: 48px;
  ${h6Css}
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  background: ${({ theme }) => theme.colors.coreLight};
  padding: 20px 16px 16px 16px;

  svg {
    transform: rotate(90deg);
    margin-bottom: 2px;

    path {
      stroke: ${({ theme }) => theme.colors.secondaryDarkBlue};
    }
  }

  &:disabled {
    display: none;
  }
`;

const ArrowRight = styled(ArrowLeft)`
  ${h6Css}
  height: 48px;
  background: ${({ theme }) => theme.colors.coreLight};
  width: fit-content;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px 16px 16px 16px;

  svg {
    transform: rotate(-90deg);
    margin-bottom: 2px;
  }
`;

const PageBtn = styled.button<{ $active: boolean }>`
  display: flex;
  width: 48px;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.textHeading};
  border: none;
  ${h6Css}

  ${({ theme, $active }) =>
    $active
      ? `
  background: ${theme.colors.coreDark};

  `
      : `
  background: ${theme.colors.coreLight};
  cursor: pointer;
  `};
`;

const Dots = styled.span`
  font-size: 30px;
  color: ${({ theme }) => theme.colors.textHeading};
  font-family: "Minion Pro";
  display: flex;
  padding: 8px;
  margin-bottom: 8px;
`;

const S = {
  Inner,
  FilterWrapper,
  H5,
  InputsWrapper,
  SelectWrapper,
  Select,
  SearchWrapper,
  SearchInput,
  SearchContainer,
  SearchButton,
  CardsWrapper,
  TitleWrapper,
  H2,
  DetailsLarge,
  PageBtn,
  Pagination,
  ArrowLeft,
  ArrowRight,
  Dots,
};

export default S;
